import { DateText } from '@/components/common/date-time-text';
import { DataText } from '@/components/typography/data-text';
import { QuantityText } from '@/components/typography/quantity-text';
import { useAppConfig } from '@/features/acl/acl-provider';
import { useCustomerMode } from '@/features/customer-mode/hooks/use-customer-mode';
import { QuoteLink } from '@/features/quote-order-detail/components/quote-order-link';
import { OrderStatusBadge } from '@/features/statuses/components/order-status-badge';
import { toCurrencyStringOrEmpty } from '@/lib/currency-helpers';
import { useLocale } from '@/lib/hooks/use-locale';
import type { GetManyQuotesOrdersListContaingProductListItem } from '@/server/types/infer-trpc-types';
import type { SrOrderStatus } from '@plato/data/starrocks/types';
import { SrSalesOrderType } from '@plato/data/starrocks/types';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MarginPercentage } from '../components/margin-percentage';

export const useQuoteColumnsForProductDetail = () => {
    const { t, locale } = useLocale();
    const { customerViewToggled } = useCustomerMode();
    const { canReadGrossMargin } = useAppConfig();

    const quoteColumnsForProductDetail: ColumnDef<GetManyQuotesOrdersListContaingProductListItem>[] = useMemo(
        () => [
            {
                accessorKey: 'customer_name',
                header: t('table_header__customer', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row, cell }) => (
                    <Link
                        className="font-medium hover:underline"
                        to={`/customers/${cell.row.original.customer_id}`}
                    >
                        <span className="text-blue-700">{row.original.customer_id}</span>
                        {` ${cell.renderValue()}`}
                    </Link>
                ),
                size: 250,
            },
            {
                accessorKey: 'order_id',
                header: t('table_header__quote_id', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => <QuoteLink transactionId={row.original.order_id} />,
            },
            {
                accessorKey: 'status',
                header: t('table_header__status', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => (
                    <OrderStatusBadge
                        status={row.original.status as SrOrderStatus}
                        type={SrSalesOrderType.QUOTE}
                    />
                ),
            },
            {
                accessorKey: 'entry_date',
                header: t('table_header__quote_created', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => <DateText date={row.original.entry_date} />,
            },
            {
                accessorKey: 'item_quantity_in_order',
                header: t('table_header__quantity', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => (
                    <QuantityText
                        quantity={row.original.item_quantity_in_order}
                        unit={row.original.quantity_unit_sales}
                    />
                ),
            },

            {
                accessorKey: 'product_unit_price',
                header: t('table_header__price', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => toCurrencyStringOrEmpty(row.original.product_unit_price, locale),
            },
            {
                accessorKey: 'product_total_in_order',
                header: t('table_header__total', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => toCurrencyStringOrEmpty(row.original.product_total_in_order, locale),
            },
            ...(customerViewToggled || !canReadGrossMargin
                ? []
                : ([
                      {
                          accessorKey: 'product_gross_margin_in_order',
                          header: t('table_header__margin', {
                              ns: 'quotes-orders-list',
                          }),
                          cell: ({ row }) =>
                              toCurrencyStringOrEmpty(row.original.product_gross_margin_in_order, locale),
                      },
                      {
                          accessorKey: 'product_gross_margin_percent',
                          header: t('table_header__margin_percent', {
                              ns: 'quotes-orders-list',
                          }),
                          cell: ({ row }) => (
                              <DataText>
                                  <MarginPercentage
                                      margin={row.original.product_gross_margin_in_order}
                                      revenue={row.original.product_total_in_order}
                                  />
                              </DataText>
                          ),
                      },
                  ] as ColumnDef<GetManyQuotesOrdersListContaingProductListItem>[])),
        ],
        [t, canReadGrossMargin, customerViewToggled, locale],
    );

    return quoteColumnsForProductDetail;
};
